/**
 * Tenant branding color tokens — shared by CSS variables and inline styles.
 */

function normalizeHex(hex) {
  if (!hex || typeof hex !== 'string') {
    return null;
  }

  const value = hex.trim();
  const match = value.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) {
    return null;
  }

  let digits = match[1].toLowerCase();
  if (digits.length === 3) {
    digits = digits
      .split('')
      .map((char) => char + char)
      .join('');
  }

  return `#${digits}`;
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex);
  if (!normalized) {
    return null;
  }

  const digits = normalized.slice(1);
  return {
    r: parseInt(digits.slice(0, 2), 16),
    g: parseInt(digits.slice(2, 4), 16),
    b: parseInt(digits.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const clamp = (value) => Math.max(0, Math.min(255, Math.round(value)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Darken a hex color by ratio (0–1). */
export function darkenHex(hex, ratio = 0.12) {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return hex;
  }

  const factor = 1 - ratio;
  return rgbToHex({
    r: rgb.r * factor,
    g: rgb.g * factor,
    b: rgb.b * factor,
  });
}

/** Relative luminance (WCAG) — higher means lighter. */
function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return 0;
  }

  const channel = (value) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  };

  return (
    0.2126 * channel(rgb.r)
    + 0.7152 * channel(rgb.g)
    + 0.0722 * channel(rgb.b)
  );
}

/** Pick readable text on top of a brand color. */
export function contrastTextOn(hex) {
  return relativeLuminance(hex) > 0.62 ? '#212121' : '#ffffff';
}

/**
 * @param {{
 *   primaryColor?: string,
 *   accentColor?: string,
 *   primaryHoverColor?: string,
 * } | null | undefined} branding
 */
export function resolveBrandingColors(branding) {
  const primaryColor = normalizeHex(branding?.primaryColor) || '#666666';
  const accentColor = normalizeHex(branding?.accentColor) || primaryColor;
  const primaryHoverColor =
    normalizeHex(branding?.primaryHoverColor) || darkenHex(primaryColor, 0.14);
  const primaryContrastColor = contrastTextOn(primaryColor);
  const rgb = hexToRgb(primaryColor);
  const primaryRgb = rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : '102, 102, 102';

  return {
    primaryColor,
    accentColor,
    primaryHoverColor,
    primaryContrastColor,
    primaryRgb,
  };
}
